// src/common/components/form/DynamicForm.tsx
import React, { useEffect, useCallback, useState, forwardRef } from 'react';
import { Breadcrumb } from 'antd';
import {
    Form, Input, InputNumber, Select, Checkbox, Radio, Switch,
    DatePicker, Button, Row, Col, Space, Card,
    message,
    Spin,
    Skeleton,
    type UploadFile,
    Alert,
    Modal,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { useForm, type FormInstance } from 'antd/es/form/Form';
import type { Rule } from 'antd/es/form'; // Type for Ant Design form rules
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat'; // Day.js plugin for better formatting
import { useAppMutation } from '@/common/hooks/useAppMutation'; // Corrected import statement
import { post, get } from '@/common/services/httpClient';
import { useNavigate, useRouter } from '@tanstack/react-router';
import FileUploadField from './FileUploadField';
import validationPatterns from './ValidationPatterns';
import {
    type ApiResponse,
    type FormField,
    type FormSection,
    isApiResponseError,
    type FieldCondition,
} from './DynamicFormTypes';
import { useTranslation } from 'react-i18next';
import { useNavigationBlocker } from '@/common/hooks/useNavigationBlocker';
// Extend dayjs with localizedFormat plugin for consistent date handling
dayjs.extend(localizedFormat);

const { Option } = Select;
const { Group: RadioGroup } = Radio;

// Define a type for the options used in Select, Checkbox, Radio
type SelectOption = { label: string; value: string | number; ISOCode?: string };
type FieldValue = string | number | boolean | dayjs.Dayjs | null | undefined | (string | number)[];
// Overall Dynamic Form Schema
export type DynamicFormSchema = {
    layout?: 'horizontal' | 'vertical' | 'inline'; // Overall form layout
    fields?: FormField[]; // Use this if no sections are defined (treated as a single default section)
    sections?: FormSection[]; // Use this for structured forms with sections
    submitButtonText?: string;
    showResetButton?: boolean; // Option to show a reset button
};

// Props for the DynamicForm component
type BreadcrumbItem = { label: string; href?: string };
type Props = {
    schema: DynamicFormSchema;
    submitApiUrl?: string;
    updateApiUrl?: string;
    getByIdApiUrl?: string;
    getByIdName?: string;
    onSuccess?: (data: unknown) => void;
    refreshTableDataKey?: string[];
    refreshSecondKey?: string[];
    onSubmit?: (values: Record<string, unknown>) => void;
    initialValues?: Record<string, unknown>;
    backUrl?: string;
    breadcrumbs?: BreadcrumbItem[]; // Optional prop for dynamic breadcrumbs
    ref?: React.Ref<any>; // Add ref prop
    onValuesChange?: (changedValues: Record<string, unknown>) => void; // Callback for form value changes
};


// --- DynamicForm Component ---

const DynamicFormComponent = ({
    schema,
    submitApiUrl,
    updateApiUrl,
    getByIdApiUrl,
    getByIdName,
    onSuccess,
    refreshTableDataKey,
    refreshSecondKey,
    onSubmit,
    initialValues: propInitialValues = {},
    backUrl = "..",
    breadcrumbs,
    onValuesChange: onValuesChangeCallback
}: Props, ref: React.Ref<any>) => {
    const [form] = useForm();

    // Expose the form instance through ref
    React.useImperativeHandle(ref, () => ({
        getFieldValue: (fieldName: string) => form.getFieldValue(fieldName),
        setFieldValue: (fieldName: string, value: unknown) => form.setFieldsValue({ [fieldName]: value }),
        getForm: () => form,
    }), [form]);
    const { t } = useTranslation();
    const [dynamicFieldOptions, setDynamicFieldOptions] = useState<Record<string, SelectOption[]>>({}); // Changed Option[] to SelectOption[]
    const [loadingDynamicOptions, setLoadingDynamicOptions] = useState<Record<string, boolean>>({});
    const [dynamicOptionsFetched, setDynamicOptionsFetched] = useState<Record<string, boolean>>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [apiErrorData, setApiErrorData] = useState<any[] | null>(null); // State to hold API error data
    const [formValues, setFormValues] = useState<Record<string, unknown>>({}); // State to hold current form values
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    // Local state for unsaved changes - no global store
    const [isDirty, setIsDirty] = useState(false);

    // Use navigation blocker hook (won't affect parent components)
    // This hook intercepts ALL navigation (links, router.navigate, history.back, etc.)
    useNavigationBlocker(isDirty);

    const { state } = useRouter().state.location;
    const record = (state as { record?: Record<string, unknown> })?.record;
    const id = record?.id?.toString();
    const isEdit = id !== undefined && id !== null; // Derive isEdit based on id presence
    const navigate = useNavigate();
    const router = useRouter();
    // Initial form vlaues for history
    const [initialFormValues, setInitialFormValues] = useState<Record<string, unknown>>({});

    // --- Mutation Hook for API Calls ---
    const mutation = useAppMutation(
        (values: Record<string, unknown>) => {
            if (isEdit && updateApiUrl) {
                return post(updateApiUrl, { ...values, id });
            } else if (submitApiUrl) {
                return post(submitApiUrl, values);
            }
            return Promise.reject(new Error("No submit or update API URL provided for form submission."));
        },
        {
            // successMessage: isEdit ? 'Updated successfully' : 'Saved successfully',
            invalidateQueryKey: refreshTableDataKey,
            SecondQueryKey: refreshSecondKey,
            form,
            onSuccess: (data: unknown) => { // Explicitly type data as unknown
                console.log('is Error data ', data);
                if (isApiResponseError(data) && data.data) {
                    setApiErrorData(data.data);
                    setApiErrorData(data.data);
                    message.error(data.message || 'An error occurred');
                    return;
                }

                if ((data as any)?.isError) {
                    message.error((data as any).message || "An error occurred");
                    return;
                }
                onSuccess?.(data);

                setSuccessMessage((data as any).message);
                setSuccessModalOpen(true);

                if (!isEdit) {
                    form.resetFields(); // Reset form only for creation, not edit
                }
            },
            onError: (error: unknown) => {
                console.error("DynamicForm - API onError:", error);
                message.error(`Submission failed: ${(error as Error).message || 'Unknown error'}`);
            }
        }
    );

    // --- Helper function to fetch dynamic options for a single field ---
    const fetchDynamicOptions = useCallback(async (field: FormField, currentFormValues: Record<string, unknown>) => {
        if (!field.dynamicOptions) return;

        const { url, method = 'GET', body, transformData, dependsOn } = field.dynamicOptions;

        // If the field depends on other fields, check if they have values.
        if (dependsOn) {
            const allDependenciesMet = dependsOn.every(depName => {
                const depValue = currentFormValues[depName];
                return depValue !== undefined && depValue !== null && depValue !== '';
            });

            if (!allDependenciesMet) {
                console.log(`DynamicForm: Dependencies for '${field.name}' not met. Clearing options.`);
                setDynamicFieldOptions(prev => ({ ...prev, [field.name]: [] }));
                return; // Don't fetch if dependencies are not met
            }
        }

        let finalUrl = url;
        // Append dependency values to URL for GET requests
        if (method === 'GET' && dependsOn) {
            const queryParams = new URLSearchParams();
            dependsOn.forEach(depName => {
                const depValue = currentFormValues[depName];
                if (depValue) {
                    queryParams.append(depName, String(depValue));
                }
            });
            if (queryParams.toString()) {
                finalUrl = `${url}${url.includes('?') ? '&' : '?'}${queryParams.toString()}`;
            }
        }

        console.log(`DynamicForm: Fetching options for field '${field.name}' from URL: ${finalUrl}`);
        if (method === 'POST' && body) {
            console.log(`DynamicForm: POST body for '${field.name}':`, body);
        }

        setLoadingDynamicOptions(prev => ({ ...prev, [field.name]: true }));
        setDynamicOptionsFetched(prev => ({ ...prev, [field.name]: true }));
        try {
            let responseData: unknown;
            if (method === 'POST') {
                responseData = await post(finalUrl, body);
            } else {
                responseData = await get(finalUrl);
            }
            console.log(`DynamicForm: Raw API response for '${field.name}':`, responseData);

            const transformFunctionBody = transformData;
            const transformFunc = new Function('apiResponse', `return ${transformFunctionBody}`)(responseData);

            if (typeof transformFunc === 'function') {
                const transformedOptions = transformFunc((responseData as ApiResponse).data || responseData);
                setDynamicFieldOptions(prev => ({ ...prev, [field.name]: transformedOptions }));
            } else {
                console.error(`DynamicForm: transformData for field '${field.name}' did not result in a function.`);
                // message.error(`Failed to transform options for ${field.label}.`);
            }
        } catch (error) {
            console.error(`DynamicForm - Error fetching dynamic options for ${field.name}:`, error);
            // message.error(`Failed to load options for ${field.label}: ${(error as Error).message || 'Unknown error'}`);
            setDynamicFieldOptions(prev => ({ ...prev, [field.name]: [] }));
        } finally {
            setLoadingDynamicOptions(prev => ({ ...prev, [field.name]: false }));
        }
    }, []); // No dependencies, it's a self-contained utility now

    // --- New useEffect for initial loading of dynamic options ---
    useEffect(() => {
        const allFields = schema.sections?.flatMap(section => section.fields) || schema.fields || [];
        allFields.forEach(field => {
            // Only fetch for non-dependent fields on initial load
            if (field.type === 'select' && field.dynamicOptions && !field.dynamicOptions.dependsOn) {
                fetchDynamicOptions(field, form.getFieldsValue());
            }
        });
    }, [schema.sections, schema.fields, fetchDynamicOptions, form]);

    // --- Effect for Initial Data Loading (Edit Mode) ---
    useEffect(() => {
        if (id && isEdit && getByIdApiUrl) {
            setLoading(true);
            console.log('DynamicForm - useEffect - Fetching data for ID:', id);
            get<ApiResponse>(`${getByIdApiUrl}/${id}`)
                .then(async (response) => {
                    console.log('DynamicForm - useEffect - Raw API response data:', response);
                    if (response && response.data) {
                        const processedData: Record<string, unknown> = {};

                        // Flatten all fields from schema (whether in sections or top-level)
                        const allSchemaFields = schema.sections?.flatMap(s => s.fields) || schema.fields || [];

                        allSchemaFields.forEach(field => {
                            const apiLookupKey = field.type === 'fileUpload' && field.updateFieldName
                                ? field.updateFieldName
                                : field.name;

                            const apiKey = Object.keys(response.data).find(
                                key => key.toLowerCase() === apiLookupKey.toLowerCase()
                            );
                            // const apiKey = Object.keys(response.data).find(
                            //     key => key.toLowerCase() === field.name.toLowerCase()
                            // );

                            if (apiKey) {
                                const value = response.data[apiKey];
                                const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
                                // Robust date/time handling for initial values
                                if (field.type === 'fileUpload' && typeof value === 'string' && value) {
                                    processedData[field.name] = [{
                                        uid: '-1',
                                        name: value,
                                        status: 'done',
                                        url: `${VITE_API_BASE_URL.replace(/api$/, '')}${value}`,
                                        // Robust date/time handling for initial values
                                    }];
                                }
                                else if (['date', 'datepicker'].includes(field.type) && typeof value === 'string' && value) {
                                    const parsedDayjs = dayjs(value); // Allow parsing of ISO 8601 format
                                    if (parsedDayjs.isValid()) {
                                        processedData[field.name] = parsedDayjs;
                                    } else {
                                        console.warn(`DynamicForm: Invalid date string '${value}' for field '${field.name}'. Setting to undefined.`);
                                        processedData[field.name] = undefined;
                                    }
                                } else if (value === null || value === '') {
                                    processedData[field.name] = undefined; // Treat null/empty string as undefined for AntD
                                } else {
                                    processedData[field.name] = value;
                                }
                            }
                        });

                        const dataWithId = { ...processedData, id };
                        console.log('DynamicForm - useEffect - Data being set to form:', dataWithId);
                        form.setFieldsValue(dataWithId);
                        setFormValues(dataWithId); // Initialize formValues state with ID
                        //Initial form vlaues for history
                        setInitialFormValues(dataWithId);
                        // After setting initial values, fetch options for dependent fields
                        for (const field of allSchemaFields) {
                            if (field.dynamicOptions?.dependsOn) {
                                await fetchDynamicOptions(field, dataWithId);
                            }
                        }
                    }
                })
                .catch(error => {
                    console.error("DynamicForm - Error fetching data:", error);
                    //message.error(`Failed to load data: ${(error as Error).message || 'Unknown error'}`);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            // For create mode, combine schema defaults with prop initial values.
            const allSchemaFields = schema.sections?.flatMap(s => s.fields) || schema.fields || [];
            const defaultValuesFromSchema = allSchemaFields.reduce((acc, field) => {
                if (field.defaultValue !== undefined) {
                    acc[field.name] = field.defaultValue;
                }
                return acc;
            }, {} as Record<string, unknown>);

            const combinedInitialValues = { ...defaultValuesFromSchema, ...propInitialValues };

            if (Object.keys(combinedInitialValues).length > 0) {
                form.setFieldsValue(combinedInitialValues);
                setFormValues(combinedInitialValues); // Initialize formValues state
            }
        }
    }, [id, isEdit, getByIdApiUrl, form, schema.fields, schema.sections, getByIdName, fetchDynamicOptions]);


    // --- Helper to Check Field Conditions (for dependencies, readOnlyWhen, disabledWhen) ---
    const checkCondition = useCallback((conditions: FieldCondition[] | undefined, currentFormValues: Record<string, unknown>): boolean => {
        if (!conditions || conditions.length === 0) return false;
        return conditions.some(condition => { // Returns true if ANY condition is met
            const fieldVal = currentFormValues[condition.field];
            console.log(`DEBUG: checkCondition - Evaluating condition for field '${condition.field}':`);
            console.log(`    Operator: '${condition.operator}', Expected Value: '${condition.value}', Actual Field Value: '${fieldVal}' (Type: ${typeof fieldVal})`);
            switch (condition.operator) {
                case '===': return fieldVal === condition.value;
                case '!==': return fieldVal !== condition.value;
                case '>': return (fieldVal as number) > (condition.value as number); // Type assertion
                case '<': return (fieldVal as number) < (condition.value as number); // Type assertion
                case '>=': return (fieldVal as number) >= (condition.value as number); // Type assertion
                case '<=': return (fieldVal as number) <= (condition.value as number); // Type assertion
                case 'includes': return Array.isArray(fieldVal) && (fieldVal as unknown[]).includes(condition.value);
                case 'isTruthy': return !!fieldVal;
                case 'isFalsy': return !fieldVal;
                default: return false;
            }
        });
    }, []);

    // --- Renders the appropriate Ant Design component for a FormField ---
    const renderAntdComponent = useCallback((field: FormField, formInstance: FormInstance) => {
        // Hide on edit logic
        if ((field.hideOnEdit && isEdit) || (field.hideOnAdd && !isEdit)) {
            const currentValue = formInstance.getFieldValue(field.name);
            if (currentValue !== undefined) {
                formInstance.setFieldsValue({ [field.name]: undefined });
            }
            return null;
        }

        const translatedPlaceholder = field.placeholder ? t(`form.placeholder.${field.name}`, { defaultValue: field.placeholder }) : (field.label ? t('form.placeholder.enter', { field: t(`form.${field.name}`, { defaultValue: field.label }) }) : '');

        const commonProps = {
            placeholder: translatedPlaceholder,
            disabled: field.disabled || loadingDynamicOptions[field.name] || (field.disableOnEdit && isEdit), // Disable if explicitly disabled or loading options
            autoComplete: field.autocomplete || 'off',
            // Pass onChange directly if defined in schema, handling various Ant Design event types
            onChange: (e: FieldValue | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | React.ChangeEvent<HTMLSelectElement> | React.ChangeEvent<HTMLButtonElement> | unknown) => { // Changed any to unknown
                if (field.onChange) {
                    if (field.type === 'input' || field.type === 'textarea' || field.type === 'password') {
                        field.onChange((e as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>).target.value);
                    } else if (field.type === 'radio') {
                        field.onChange(e); // RadioChangeEvent has target.value, but the event itself is passed
                    } else if (field.type === 'number' || field.type === 'switch' || field.type === 'select' || field.type === 'checkbox' || field.type === 'date' || field.type === 'datepicker') {
                        field.onChange(e); // Value is passed directly for these components
                    } else {
                        field.onChange(e); // Fallback for other potential types
                    }
                }
            },
        };

        // Apply conditional readOnly/disabled based on current form values
        const shouldBeReadOnly = checkCondition(field.readOnlyWhen, formValues); // Use formValues state
        const shouldBeDisabled = checkCondition(field.disabledWhen, formValues); // Use formValues state

        // If readOnly is forced, it implies disabled for non-input types
        if (shouldBeReadOnly) {
            if (['input', 'textarea', 'password', 'number'].includes(field.type)) {
                Object.assign(commonProps, { readOnly: true });
            } else {
                Object.assign(commonProps, { disabled: true }); // For other types like Select, Switch, readOnly means disabled interaction
            }
        }
        // Explicitly set disabled if disabledWhen condition is met, or if readOnly implied disabled
        if (shouldBeDisabled || shouldBeReadOnly) {
            Object.assign(commonProps, { disabled: true });
        }

        // Render the specific Ant Design component based on field type
        switch (field.type) {
            case 'input':
                return <Input {...commonProps} />;
            case 'textarea':
                return <Input.TextArea rows={4} {...commonProps} />;
            case 'password':
                return <Input.Password {...commonProps} />;
            case 'number':
                return <InputNumber {...commonProps} style={{ width: '100%' }} size='large' />;
            case 'switch':
                return <Switch {...commonProps} />;
            case 'select': {
                const optionsToRenderForSelect = dynamicFieldOptions[field.name] || field.options || [];
                const defaultSize = 'large';
                const defaultMode = undefined;
                const isLoading = loadingDynamicOptions[field.name];
                const showEmptyError = field.dynamicOptions?.onEmpty && optionsToRenderForSelect.length === 0 && dynamicOptionsFetched[field.name] && !isLoading;
                const disableBecauseEmpty = !!showEmptyError;
                return (
                    <>
                        <Select
                            {...commonProps}
                            disabled={!!commonProps.disabled || disableBecauseEmpty}
                            allowClear
                            loading={isLoading}
                            onDropdownVisibleChange={(open) => {
                                if (open && field.dynamicOptions && (!dynamicFieldOptions[field.name] || dynamicFieldOptions[field.name].length === 0)) {
                                    fetchDynamicOptions(field, form.getFieldsValue());
                                }
                            }}
                            size={field.size || defaultSize}
                            mode={field.mode || defaultMode}
                            showSearch={field.showSearch}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            onSelect={(value) => {
                                form.setFieldValue("__lastSelected__", value);
                                if (field.showSearch) {
                                    const allFields = schema.sections?.flatMap(s => s.fields) || schema.fields || [];
                                    const currentFormValues = { ...form.getFieldsValue(), [field.name]: value };
                                    allFields.forEach(dependentField => {
                                        if (dependentField.dynamicOptions?.dependsOn?.includes(field.name)) {
                                            form.setFieldsValue({ [dependentField.name]: undefined });
                                            fetchDynamicOptions(dependentField, currentFormValues);
                                        }
                                    });
                                }
                            }}
                            onBlur={() => {
                                const lastSelected = form.getFieldValue("__lastSelected__");
                                const currentValue = form.getFieldValue(field.name);
                                if (!lastSelected && currentValue) {
                                    form.setFieldValue(field.name, undefined);
                                }
                                form.setFieldValue("__lastSelected__", undefined);
                            }}
                            onClear={() => {
                                form.setFieldValue(field.name, undefined);
                                form.setFieldValue("__lastSelected__", undefined);
                            }}
                        >
                            {optionsToRenderForSelect.map(opt => (
                                <Option key={opt.value} value={opt.value} label={opt.label}>
                                    {opt.label}
                                </Option>
                            ))}
                        </Select>
                        {showEmptyError && (
                            <div style={{ color: field.dynamicOptions.onEmpty.type === 'error' ? '#ff4d4f' : '#faad14', marginTop: 4 }}>
                                {field.dynamicOptions.onEmpty.message}
                            </div>
                        )}
                    </>
                );
            }
            case 'checkbox': { // Added block scope
                // For Checkbox.Group, the 'options' prop takes an array of { label, value }
                const checkboxOptions = dynamicFieldOptions[field.name] || field.options || [];
                return <Checkbox.Group options={checkboxOptions} {...commonProps} />;
            }
            case 'radio': { // Added block scope
                // For Radio.Group, options are mapped manually as before
                const radioOptions = dynamicFieldOptions[field.name] || field.options || [];
                return (
                    <RadioGroup {...commonProps}>
                        {radioOptions.map(opt => (
                            <Radio key={opt.value} value={opt.value}>{opt.label}</Radio>
                        ))}
                    </RadioGroup>
                );
            }
            case 'date':
            case 'datepicker': {
                const datePickerDisabledDate = field.disabledDateFunction
                    ? new Function('dayjs', 'form', `return ${field.disabledDateFunction}`)(dayjs, formInstance)
                    : field.disabledDate;
                return <DatePicker {...commonProps} style={{ width: '100%' }} size="large" format={{
                    format: 'DD-MM-YYYY',
                    type: 'mask',
                }} inputReadOnly disabledDate={datePickerDisabledDate} />;
            }
            case 'fileUpload':
                return (
                    <FileUploadField
                        field={field}
                        commonProps={commonProps}
                        form={formInstance}
                        value={formInstance.getFieldValue(field.name)}
                    />
                );
            default:
                return <Input {...commonProps} />;
        }
    }, [checkCondition, loadingDynamicOptions, dynamicFieldOptions, formValues, isEdit, fetchDynamicOptions, form]); // Add formValues to dependencies of this memoized function

    // --- Generates Ant Design validation rules based on FormField schema ---
    const generateRules = useCallback((field: FormField, currentFormValues: Record<string, unknown>) => {
        const rules: Rule[] = [];

        let isRequired = false;
        if (typeof field.required === 'boolean') {
            isRequired = field.required;
        } else if (Array.isArray(field.required)) {
            isRequired = checkCondition(field.required, currentFormValues);
        }

        if (isRequired) {
            rules.push({
                required: true,
                message: `${t("Mandatory")}`
                // message: (field.requiredMessage && t(field.requiredMessage)) || `${t(field.label)} ${t("Mandatory")}`
            });
        }

        // Add min rule for number inputs
        if (field.min !== undefined && field.type === 'number') {
            rules.push({
                min: field.min,
                type: 'number', // Essential for min/max to work correctly with InputNumber
                //message: `${field.label} must be at least ${field.min}`
                message: `This field must be at least ${field.min}`
            });
        }

        // Add max rule for number inputs
        if (field.max !== undefined && field.type === 'number') {
            rules.push({
                max: field.max,
                type: 'number', // Essential for min/max to work correctly with InputNumber
                //message: `${field.label} cannot be more than ${field.max}`
                message: `This field cannot be more than ${field.max}`
            });
        }

        // --- MODIFIED HERE: Handle dynamic pattern based on name or object ---
        if (field.pattern) {
            let patternRegex: string | undefined;
            let patternMessage: string | undefined;

            if (typeof field.pattern === 'string') {
                // If pattern is a string, look it up in the predefined patterns
                const predefinedPattern = validationPatterns[field.pattern];
                if (predefinedPattern) {
                    patternRegex = predefinedPattern.regex;
                    patternMessage = t(predefinedPattern.message);
                } else {
                    console.warn(`DynamicForm: Unknown pattern name '${field.pattern}' for field '${field.name}'. No pattern validation applied.`);
                }
            } else {
                // If pattern is an object, use its regex and message directly
                patternRegex = field.pattern.regex;
                patternMessage = t(field.pattern.message);
            }

            if (patternRegex) {
                rules.push({
                    pattern: new RegExp(patternRegex), // Convert string regex to RegExp object
                    message: patternMessage || `Invalid format for ${field.label}.`
                });
            }
        }

        // Add any generic validation rules provided in the schema
        if (field.validation) {
            if (Array.isArray(field.validation)) {
                rules.push(...field.validation);
            } else {
                rules.push(field.validation);
            }
        }

        return rules;
    }, [checkCondition]);

    // --- Renders a single FormField within a Col for grid layout ---
    const renderField = useCallback((field: FormField, formInstance: FormInstance) => {
        const allSchemaFields = schema.sections?.flatMap(s => s.fields) || schema.fields || [];
        const uploadTargetFields = allSchemaFields
            .filter(f => f.type === 'fileUpload' && f.updateFieldName)
            .map(f => f.updateFieldName);

        const isFieldHiddenByDependencies = field.dependencies && !checkCondition(field.dependencies, formValues); // Use formValues state

        if (isFieldHiddenByDependencies && !uploadTargetFields.includes(field.name)) {
            const currentValue = formInstance.getFieldValue(field.name);
            if (currentValue !== undefined) {
                formInstance.setFieldsValue({ [field.name]: undefined });
            }
            return null; // Don't render the field
        }

        // Hide on edit logic
        if ((field.hideOnEdit && isEdit) || (field.hideOnAdd && !isEdit)) {
            const currentValue = formInstance.getFieldValue(field.name);
            if (currentValue !== undefined) {
                formInstance.setFieldsValue({ [field.name]: undefined });
            }
            return null;
        }

        const rules = generateRules(field, formValues);
        const requiredDependencies = Array.isArray(field.required) ? field.required.map(dep => dep.field) : [];
        const translatedLabel = field.label ? t(`form.${field.name}`, { defaultValue: field.label }) : '';

        return (
            <Col span={field.colSpan || 24} key={field.name}>
                {/* Outer Form.Item can use dependencies to trigger re-render of inner Form.Item */}
                {/* This ensures conditional logic (dependencies, readOnlyWhen, disabledWhen) works */}
                <Form.Item
                    name={field.name}
                    label={translatedLabel} // Outer label is usually hidden to avoid duplicate labels
                    valuePropName={field.type === 'switch' || field.type === 'checkbox' ? 'checked' : 'value'}
                    initialValue={field.type === 'date' || field.type === 'datepicker' ?
                        (propInitialValues[field.name] ? dayjs(propInitialValues[field.name] as string | number | dayjs.Dayjs | Date | null | undefined) : undefined) : // Type assertion
                        propInitialValues[field.name]}
                    noStyle={true} // Hide the outer Form.Item's default styling
                    dependencies={(field.dependencies?.map(dep => dep.field) || [])
                        .concat(field.readOnlyWhen?.map(dep => dep.field) || [])
                        .concat(field.disabledWhen?.map(dep => dep.field) || [])
                        .concat(requiredDependencies)
                        .filter((val, index, self) => self.indexOf(val) === index)} // Unique list of dependencies
                >
                    {/* This nested Form.Item is crucial for dependency-based rendering and validation messages.
               When dependencies change, this Form.Item (and its children) will re-render,
               allowing `renderAntdComponent` to re-evaluate `checkCondition` and rules. */}
                    <Form.Item name={field.name}
                        label={translatedLabel}
                        rules={rules}
                        // valuePropName={field.type === 'switch' || field.type === 'checkbox' ? 'checked' : 'value'}
                        valuePropName={field.type === 'switch' || field.type === 'checkbox' ? 'checked' : (field.type === 'fileUpload' ? 'fileList' : 'value')}
                        getValueFromEvent={field.type === 'fileUpload' ? (e) => e?.fileList : undefined}
                        hidden={field.hidden} // Use hidden prop here for actual hidden state
                        initialValue={
                            // Re-evaluate initial value for nested Form.Item on re-render
                            field.type === 'date' || field.type === 'datepicker' ?
                                (propInitialValues[field.name] ? dayjs(propInitialValues[field.name] as string | number | dayjs.Dayjs | Date | null | undefined) : undefined) : // Type assertion
                                propInitialValues[field.name]
                        }
                    >
                        {renderAntdComponent(field, formInstance)}
                    </Form.Item>
                </Form.Item>
            </Col>
        );
    }, [checkCondition, generateRules, renderAntdComponent, isEdit, propInitialValues, formValues]); // Add formValues to dependencies


    // --- Renders a section of the form (group of fields) ---
    const renderSection = useCallback((section: FormSection, formInstance: FormInstance) => {
        const content = section.layout?.type === 'grid' ? (
            <Row gutter={section.layout.gutter || 16}>
                {section.fields.map(field => renderField(field, formInstance))}
            </Row>
        ) : (
            // If layout is not grid, use Ant Design Space for vertical stacking
            <Space direction="vertical" style={{ width: '100%' }}>
                {section.fields.map(field => renderField(field, formInstance))}
            </Space>
        );

        const translatedTitle = section.title ? t(`form.section.${section.key}`, { defaultValue: section.title }) : '';
        // Wrap in Card if section type is 'card', otherwise a simple div
        return section.type === 'card' ? (
            <Card key={section.key} title={translatedTitle} style={{ marginBottom: '24px' }} size="small">
                {content}
            </Card>
        ) : (
            <div key={section.key} style={section.title ? { marginBottom: '16px' } : {}}>
                {section.title && <h3>{translatedTitle}</h3>}
                {content}
            </div>
        );
    }, [renderField, t]); // Add t to dependencies for translation

    // select label for history
    const getLabel = (val: any, key: string, fieldConfig?: FormField) => {
        // 1️⃣ First check static schema options
        const staticLabel = fieldConfig?.options?.find(o => o.value === val)?.label;

        // 2️⃣ Then check dynamicFieldOptions (fetched from API)
        const dynamicLabel = dynamicFieldOptions[key]?.find((o: any) => o.value === val)?.label;

        return staticLabel ?? dynamicLabel ?? val;
    };


    // --- Handles form submission ---
    const onFinish = useCallback(async (values: Record<string, unknown>) => { // Changed any to unknown
        console.log('DynamicForm - Form values before processing:', JSON.stringify(values));
        const processed = { ...values };

        // vlaues for history
        if (isEdit) {
            const allSchemaFields: FormField[] =
                schema.sections?.flatMap((s) => s.fields) || schema.fields || [];

            const changedValues = Object.entries(values)
                .filter(([key, newValue]) => {
                    const oldValue = initialFormValues[key];
                    const normalize = (v: any) =>
                        dayjs.isDayjs(v) ? v.format("YYYY-MM-DD") : v;
                    return JSON.stringify(normalize(oldValue)) !== JSON.stringify(normalize(newValue));
                })
                .map(([key, newValue]) => {
                    const fieldConfig = allSchemaFields.find(f => f.name === key);
                    const oldVal = initialFormValues[key];

                    let displayOld = oldVal;
                    let displayNew = newValue;

                    if (["select", "checkbox", "radio"].includes(fieldConfig?.type || "")) {
                        if (Array.isArray(newValue)) {
                            displayNew = newValue.map(v => getLabel(v, key, fieldConfig)).join(", ");
                            displayOld = Array.isArray(oldVal)
                                ? oldVal.map(v => getLabel(v, key, fieldConfig)).join(", ")
                                : getLabel(oldVal, key, fieldConfig);
                        } else {
                            displayOld = getLabel(oldVal, key, fieldConfig);
                            displayNew = getLabel(newValue, key, fieldConfig);
                        }
                    }

                    // const normalizeDisplay = (v: any) =>
                    //     dayjs.isDayjs(v) ? v.format("YYYY-MM-DD") : v.toString();

                    const normalizeDisplay = (v: any) => {
                        if (v == null) return ""; // null or undefined
                        if (dayjs.isDayjs(v)) return v.format("YYYY-MM-DD");
                        if (typeof v === "object") return JSON.stringify(v); // handle complex objects safely
                        return String(v); // works with Arabic and all Unicode text
                    };


                    return {
                        fieldName: key,
                        oldValue: normalizeDisplay(displayOld),
                        newValue: normalizeDisplay(displayNew),
                    };
                });

            processed.auditHistory = changedValues;
        }


        // Determine all fields from schema (flat list) for processing dates and clearing hidden values
        const allSchemaFields = schema.sections?.flatMap(s => s.fields) || schema.fields || [];
        const uploadTargetFields = allSchemaFields
            .filter(f => f.type === 'fileUpload' && f.updateFieldName)
            .map(f => f.updateFieldName);

        allSchemaFields.forEach((field) => {
            // Attach  names to payload based on selected IDs

            const countryField = allSchemaFields.find(f => f.name === 'countryId');
            const cityField = allSchemaFields.find(f => f.name === 'cityId');
            const currencyField = allSchemaFields.find(f => f.name === 'currencyId');
            const policyHolderField = allSchemaFields.find(f => f.name === 'policyHolderId');
            const planField = allSchemaFields.find(f => f.name === 'planId');
            const planGroupField = allSchemaFields.find(f => f.name === 'planGroupId');
            const salesPersonField = allSchemaFields.find(f => f.name === 'salesPersonId');
            const companyField = allSchemaFields.find(f => f.name === 'companyId');
            const customerField = allSchemaFields.find(f => f.name === 'customerId');
            const serviceCategoryItemField = allSchemaFields.find(f => f.name === 'serviceCategoryItemId');

            const countryOptions = countryField ? (dynamicFieldOptions[countryField.name] || countryField.options || []) : [];
            const cityOptions = cityField ? (dynamicFieldOptions[cityField.name] || cityField.options || []) : [];
            const currencyOptions = currencyField ? (dynamicFieldOptions[currencyField.name] || currencyField.options || []) : [];
            const policyHolderOptions = policyHolderField ? (dynamicFieldOptions[policyHolderField.name] || policyHolderField.options || []) : [];
            const planOptions = planField ? (dynamicFieldOptions[planField.name] || planField.options || []) : [];
            const planGroupOptions = planGroupField ? (dynamicFieldOptions[planGroupField.name] || planGroupField.options || []) : [];
            const salesPersonOptions = salesPersonField ? (dynamicFieldOptions[salesPersonField.name] || salesPersonField.options || []) : [];
            const companyOptions = companyField ? (dynamicFieldOptions[companyField.name] || companyField.options || []) : [];
            const customerOptions = customerField ? (dynamicFieldOptions[customerField.name] || customerField.options || []) : [];
            const serviceCategoryItemOptions = serviceCategoryItemField ? (dynamicFieldOptions[serviceCategoryItemField.name] || serviceCategoryItemField.options || []) : [];

            if (processed.countryId) {
                const selectedCountry = countryOptions.find(opt => opt.value === processed.countryId);
                processed.country = selectedCountry ? selectedCountry.label : '';
            }
            if (processed.cityId) {
                const selectedCity = cityOptions.find(opt => opt.value === processed.cityId);
                processed.city = selectedCity ? selectedCity.label : '';
            }
            if (processed.currencyId) {
                const selectedCurrency = currencyOptions.find(opt => opt.value === processed.currencyId);
                processed.currency = selectedCurrency ? selectedCurrency.label : '';
                processed.currencyISOCode = selectedCurrency?.ISOCode || null;
            }
            if (processed.policyHolderId) {
                const selectedPolicyHolder = policyHolderOptions.find(opt => opt.value === processed.policyHolderId);
                processed.PolicyHolderName = selectedPolicyHolder ? selectedPolicyHolder.label : '';
            }
            if (processed.planId) {
                const selectedPlan = planOptions.find(opt => opt.value === processed.planId);
                processed.planName = selectedPlan ? selectedPlan.label : '';
                processed.currencyISOCode = selectedPlan?.ISOCode || null;
            }
            if (processed.planGroupId) {
                const selectedPlanGroup = planGroupOptions.find(opt => opt.value === processed.planGroupId);
                processed.groupName = selectedPlanGroup ? selectedPlanGroup.label : '';
            }
            if (processed.salesPersonId) {
                const selectedsalesPerson = salesPersonOptions.find(opt => opt.value === processed.salesPersonId);
                processed.salesPersonName = selectedsalesPerson ? selectedsalesPerson.label : '';
            }
            if (processed.companyId) {
                const selectedCompany = companyOptions.find(opt => opt.value === processed.companyId);
                processed.companyName = selectedCompany ? selectedCompany.label : '';
            }

            if (processed.customerId) {
                const selectedCustomer = customerOptions.find(opt => opt.value === processed.customerId);
                processed.customerName = selectedCustomer ? selectedCustomer.label : '';
            }

            if (processed.serviceCategoryItemId) {
                const selectedserviceCategoryItem = serviceCategoryItemOptions.find(opt => opt.value === processed.serviceCategoryItemId);
                processed.serviceCategoryItemName = selectedserviceCategoryItem ? selectedserviceCategoryItem.label : '';
            }

            // Format Day.js objects for date/time fields to YYYY-MM-DD string
            if (['date', 'datepicker'].includes(field.type) && processed[field.name] && dayjs.isDayjs(processed[field.name])) {
                processed[field.name] = (processed[field.name] as dayjs.Dayjs).format('YYYY-MM-DD');
            }
            if (field.type === 'fileUpload' && Array.isArray(processed[field.name])) {
                const fileList = processed[field.name] as UploadFile[];
                if (fileList.length > 0 && fileList[0].status === 'done' && fileList[0].url) {
                    processed[field.name] = fileList[0].url;
                } else {
                    delete processed[field.name]; // Or set to null/undefined if appropriate
                }
            }
            // If a field was hidden by dependencies, `hideOnEdit`, or `hidden` prop,
            // explicitly delete its value from the submitted data to ensure it's not sent.
            const isFieldHiddenByDependencies = field.dependencies && !checkCondition(field.dependencies, formValues); // Use formValues state
            if ((isFieldHiddenByDependencies || (field.hideOnEdit && isEdit) || (field.hideOnAdd && !isEdit)) && !uploadTargetFields.includes(field.name)) {
                delete processed[field.name];
            }
        });

        console.log('DynamicForm - Form values submitted (after processing):', JSON.stringify(processed));

        // Execute mutation if API URLs are provided, otherwise call external onSubmit
        if (submitApiUrl || updateApiUrl) {
            mutation.mutate(processed);
        } else {
            onSubmit?.(processed); // Use the external onSubmit prop if provided
        }
        setIsDirty(false); // Reset dirty state on successful submit
    }, [mutation, submitApiUrl, updateApiUrl, onSubmit, schema.fields, schema.sections, isEdit, checkCondition, formValues]);

    const handleValuesChange = (changedValues: Record<string, unknown>, allValues: Record<string, unknown>) => {
        const newFormValues = { ...allValues, id: formValues.id };
        setFormValues(newFormValues);
        setIsDirty(true); // Mark form as dirty if any value changes
        // console.log("Changed:", changedValues); 

        const changedFieldName = Object.keys(changedValues)[0];
        if (!changedFieldName) return;

        const allFields = schema.sections?.flatMap(s => s.fields) || schema.fields || [];

        // Check if the changed field is a dependency for any other field.
        const changedField = allFields.find(f => f.name === changedFieldName);
        if (!changedField?.showSearch) {
            const dependentFields = allFields.filter(field => field.dynamicOptions?.dependsOn?.includes(changedFieldName));

            // First reset dependent fields and update local form values so fetches use fresh data.
            dependentFields.forEach(field => {
                form.setFieldsValue({ [field.name]: undefined });
                newFormValues[field.name] = undefined;
            });

            // Then refetch options for dependent fields with updated values.
            dependentFields.forEach(field => {
                fetchDynamicOptions(field, newFormValues);
            });
        }

        // Call the optional callback provided by parent component
        onValuesChangeCallback?.(changedValues);
    };

    // Determine which fields/sections to render:
    // If 'sections' are defined, use them. Otherwise, treat top-level 'fields' as a single default section.
    const sectionsToRender = schema.sections && schema.sections.length > 0
        ? schema.sections
        : [{ key: 'default-section', title: '', type: 'group', fields: schema.fields || [], layout: { type: 'grid', gutter: 16 } } as FormSection]; // Explicitly cast to FormSection
    const handleBack = () => {
        // Simply navigate - useNavigationBlocker hook will intercept if isDirty is true
        if (backUrl === 'history') {
            router.history.back();
        } else if (backUrl) {
            navigate({ to: backUrl, skipBlock: true } as never);
        }
    };
    // --- Dynamic Breadcrumbs from URL if not provided ---
    let breadcrumbItems: BreadcrumbItem[] = [];
    if (breadcrumbs && breadcrumbs.length > 0) {
        breadcrumbItems = breadcrumbs;
    } else {
        // Use location from TanStack Router
        const path = router.state.location.pathname || window.location.pathname;
        // Remove leading/trailing slashes, split by '/'
        const segments = path.replace(/^\/+|\/+$/g, '').split('/');
        let runningPath = '';
        breadcrumbItems = segments.map((seg, idx) => {
            runningPath += '/' + seg;
            // Capitalize and replace dashes/underscores with spaces
            const label = seg.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            let href: string | undefined = undefined;
            if (segments.length === 2 && idx === 0) {
                href = runningPath;
            } else if (segments.length > 2 && idx > 0 && idx < segments.length - 1) {
                href = runningPath;
            }
            return {
                label,
                href: idx === segments.length - 1 ? undefined : href
            };
        });
    }

    const getBreadcrumbLabel = (label: string) =>
        t(`breadcrumb.${label}`, { defaultValue: label });

    return (
        <>
            {/* Breadcrumb and Go Back button in one row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <Breadcrumb>
                    {breadcrumbItems.map((item, idx) =>
                        item.href ? (
                            //<Breadcrumb.Item key={idx} href={item.href}>{item.label}</Breadcrumb.Item>
                            <Breadcrumb.Item key={idx} href={item.href}>
                                {getBreadcrumbLabel(item.label)}
                            </Breadcrumb.Item>
                        ) : (
                            //<Breadcrumb.Item key={idx}>{item.label}</Breadcrumb.Item>
                            <Breadcrumb.Item key={idx}>
                                {getBreadcrumbLabel(item.label)}
                            </Breadcrumb.Item>
                        )
                    )}
                </Breadcrumb>
                <Button
                    htmlType="button"
                    onClick={handleBack}
                >
                    {t('common.goBack', { defaultValue: 'Go Back' })}
                </Button>
            </div>

            <Spin spinning={false}>
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <Skeleton active paragraph={{ rows: 3, width: '100%' }} />
                        <Skeleton active paragraph={{ rows: 3, width: '100%' }} />
                        <Skeleton active paragraph={{ rows: 2, width: '100%' }} />
                    </div>
                ) : (
                    <Form
                        form={form}
                        layout={schema.layout || 'vertical'} // Overall form layout
                        onFinish={onFinish}
                        onFinishFailed={({ errorFields }) => {
                            form.scrollToField(errorFields[0].name, {
                                behavior: 'smooth',
                                block: 'center',
                            });
                        }} // Form submission handler
                        initialValues={propInitialValues} // Initial values for the form (can be overridden by fetched data)
                        onValuesChange={handleValuesChange}
                    >
                        {/* Render each section of the form */}
                        {sectionsToRender.map(section => renderSection(section, form))}

                        {/* Submit and Reset Buttons */}
                        <Form.Item>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                <Button type="primary" htmlType="submit" loading={mutation.isPending}>
                                    {schema.submitButtonText ? t(schema.submitButtonText) : (isEdit ? t('common.update', { defaultValue: t('Update') }) : t('common.save', { defaultValue: t('Save') }))}
                                </Button>
                                {(schema.showResetButton && !isEdit) && (
                                    <Button htmlType="button" onClick={() => form.resetFields()} style={{ marginLeft: '8px' }}>

                                        {t('common.reset', { defaultValue: t('Reset') })}
                                    </Button>
                                )}
                            </div>
                        </Form.Item>
                        {apiErrorData && apiErrorData.length > 0 && (
                            <Alert
                                message="Error"
                                description={
                                    <ul>
                                        {apiErrorData.map((item, index) => (
                                            <li key={index}>
                                                <strong>{item.failedField}:</strong> {item.failureReason}
                                            </li>
                                        ))}
                                    </ul>
                                }
                                type="error"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        )}
                    </Form>
                )}
            </Spin>

            {/* Success Modal */}
            <Modal
                open={successModalOpen}
                onCancel={() => setSuccessModalOpen(false)}
                footer={null}
                closable={false}
                width={clamp(90, 420, 520)}
                style={{ backdropFilter: 'blur(4px)' }}
                bodyStyle={{ padding: '40px 32px' }}
            >
                <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
                    {/* Icon */}
                    <div
                        style={{
                            width: 72,
                            height: 72,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #10B98120 0%, #05937720 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 32,
                            border: '2px solid #D1FAE5'
                        }}
                    >
                        <CheckCircleOutlined style={{ color: '#059377' }} />
                    </div>

                    {/* Title */}
                    <div>
                        <h2 style={{ margin: '0 0 8px 0', fontSize: 20, fontWeight: 700, color: '#0F172A', letterSpacing: '-0.3px' }}>
                            {isEdit ? t('common.updatedSuccessfully', { defaultValue: 'Updated Successfully' }) : t('common.savedSuccessfully', { defaultValue: 'Saved Successfully' })}
                        </h2>
                        <p style={{ margin: 0, fontSize: 14, color: '#64748B', fontWeight: 400, lineHeight: 1.5 }}>
                            {successMessage}
                        </p>
                    </div>

                    {/* Action Button */}
                    <Button
                        type="primary"
                        onClick={() => {
                            setSuccessModalOpen(false);
                            // Navigate after closing modal
                            if (backUrl === "history") {
                                router.history.back();
                            } else if (backUrl) {
                                navigate({ to: backUrl, skipBlock: true } as never);
                            }
                        }}
                        style={{
                            borderRadius: 10,
                            padding: '10px 32px',
                            fontSize: 14,
                            fontWeight: 600,
                            height: 40,
                            minWidth: 140,
                            background: '#059377',
                            borderColor: '#059377'
                        }}
                    >
                        {t('common.close', { defaultValue: 'Close' })}
                    </Button>
                </div>
            </Modal>
        </>
    );
};

function clamp(min: number, val: number, max: number) {
    return Math.min(Math.max(val, min), max);
}

export default forwardRef(DynamicFormComponent);